/**
 * Time-display helpers for the live ETA card / row.
 *
 * Pure functions only — easy to unit-test, no platform dependencies.
 *
 * @module utils/time-display
 */

const ACTIVE_LIVE_STATUSES = new Set(["picking_up", "at_hub", "ready_to_return", "returning"])

interface OrderLikeForTime {
  serviceType?: string
  status?: string
  projectedCompletionAtLive?: string | null
  scheduledReturnTime?: string | Date | null
  spatesteRuckgabe?: string | null
}

/**
 * Returns the best-available ISO timestamp for "when the customer gets the car back".
 *
 * Priority:
 *   1. projectedCompletionAtLive (digital-twin projection, kept fresh by live-eta.ts during driving)
 *   2. scheduledReturnTime       (HubSpot SLA, available pre-pickup)
 *   3. null                      (no data — caller hides the value)
 *
 * Note: spatesteRuckgabe is a wall-clock HH:MM string without a date and is intentionally
 * NOT coalesced here — it's used as the FleetHub fallback display directly because it's
 * what the row already showed.
 */
export function coalesceProjectedTime(order: OrderLikeForTime): string | null {
  if (order.projectedCompletionAtLive) return order.projectedCompletionAtLive
  if (order.scheduledReturnTime) {
    return order.scheduledReturnTime instanceof Date
      ? order.scheduledReturnTime.toISOString()
      : order.scheduledReturnTime
  }
  return null
}

/**
 * Compute remaining minutes from a projected ISO timestamp.
 * Clamped to zero — the card never shows negative minutes once the projection has elapsed.
 */
export function computeRemainingMinutes(projectedISO: string, now: number): number {
  const projectedMs = Date.parse(projectedISO)
  if (Number.isNaN(projectedMs)) return 0
  return Math.max(0, Math.round((projectedMs - now) / 60_000))
}

/**
 * The live indicator only pulses for orders that are:
 *  - pickup_delivery service (driveby has no return leg)
 *  - in an active fulfillment status
 *  - have a projected completion timestamp set (otherwise nothing's "live")
 */
export function isLive(order: OrderLikeForTime): boolean {
  if (order.serviceType !== "pickup_delivery") return false
  if (!order.status || !ACTIVE_LIVE_STATUSES.has(order.status)) return false
  if (!order.projectedCompletionAtLive) return false
  return true
}

// ---------------------------------------------------------------------------
// Phase-aware pickup / return label selectors (ADR-0012 §6)
// ---------------------------------------------------------------------------

/**
 * Discriminates how the pickup timestamp should be labelled.
 *
 * - `projected_door_arrival` — live projection, PA driving to customer ("Abholung gegen HH:MM")
 * - `scheduled_sla_ceiling`  — HubSpot SLA ceiling fallback ("Abholung bis HH:MM")
 * - `realised_pickup`        — confirmed handover, past-tense ("Abgeholt um HH:MM")
 * - `none`                   — hide pickup row (driveby orders have no pickup leg)
 */
export type PickupLabelKind =
  | "projected_door_arrival"
  | "scheduled_sla_ceiling"
  | "realised_pickup"
  | "none"

export interface PickupLabelResult {
  iso: string | null
  kind: PickupLabelKind
}

/**
 * Discriminates how the return timestamp should be labelled.
 *
 * - `projected` — live or plan-derived projection ("Rückgabe gegen HH:MM")
 * - `scheduled` — HubSpot SLA ceiling fallback ("Rückgabe bis HH:MM")
 * - `actual`    — recorded completion time ("Rückgabe um HH:MM"); PLA-307
 */
export type ReturnLabelKind = "projected" | "scheduled" | "actual"

export interface ReturnLabelResult {
  iso: string | null
  kind: ReturnLabelKind
}

interface OrderLikeForPickupLabel {
  serviceType?: string | null
  realisedPickupAt?: string | null
  projectedPickupAtLive?: string | null
  scheduledPickupTime?: string | null
}

interface OrderLikeForReturnLabel {
  status?: string | null
  completedAt?: string | null
  projectedCompletionAtLive?: string | null
  scheduledReturnTime?: string | Date | null
}

/**
 * Select the primary pickup label for a customer-facing surface.
 *
 * ADR-0012 §6 display hierarchy:
 *
 * Driveby orders have no pickup leg — kind: "none".
 *
 * Pre-pickup (realisedPickupAt absent):
 *   projectedPickupAtLive → scheduledPickupTime → null
 *
 * Post-pickup (realisedPickupAt set):
 *   realisedPickupAt (kind: "realised_pickup").
 *   The frozen projectedPickupAtLive is NOT shown on customer surfaces post-pickup.
 */
export function selectPickupLabel(order: OrderLikeForPickupLabel): PickupLabelResult {
  // Driveby orders have no pickup leg (ADR-0012 §7).
  if (order.serviceType === "driveby") {
    return { iso: null, kind: "none" }
  }

  // Post-pickup: surface the confirmed handover time (ADR-0012 §6 post-pickup branch).
  if (order.realisedPickupAt) {
    return { iso: order.realisedPickupAt, kind: "realised_pickup" }
  }

  // Pre-pickup: live projection takes priority over SLA ceiling.
  if (order.projectedPickupAtLive) {
    return { iso: order.projectedPickupAtLive, kind: "projected_door_arrival" }
  }

  if (order.scheduledPickupTime) {
    return { iso: order.scheduledPickupTime, kind: "scheduled_sla_ceiling" }
  }

  return { iso: null, kind: "scheduled_sla_ceiling" }
}

/**
 * Select the return label for a customer-facing surface.
 *
 * ADR-0012 §6 — same hierarchy applies both pre- and post-pickup:
 *   projectedCompletionAtLive → scheduledReturnTime → null
 *
 * PLA-307 extension: when the order is completed AND completedAt is present,
 * the actual recorded time takes priority over all estimates.
 */
export function selectReturnLabel(order: OrderLikeForReturnLabel): ReturnLabelResult {
  // PLA-307: completed orders with a recorded actual completion time surface
  // the exact timestamp instead of an estimate.
  if (order.status === "completed" && order.completedAt) {
    return { iso: order.completedAt, kind: "actual" }
  }

  if (order.projectedCompletionAtLive) {
    return { iso: order.projectedCompletionAtLive, kind: "projected" }
  }

  if (order.scheduledReturnTime) {
    const iso =
      order.scheduledReturnTime instanceof Date
        ? order.scheduledReturnTime.toISOString()
        : order.scheduledReturnTime
    return { iso, kind: "scheduled" }
  }

  return { iso: null, kind: "scheduled" }
}
